import type {
  WidgetLaunchOptions,
  WidgetOptions,
  WidgetQueryParams,
} from "@app-contexts/sdk/services/Widget";
import { CUSTOMER_ATTRIBUTION_QUERY_PARAM } from "@shared/modules/basket/session/customer-attribution-messaging";

type EmbeddedWidgetOptions =
  | Pick<WidgetOptions, "enableIframeResize">
  | Record<string, never>;

type EmbeddedWidgetCallArgs = [
  containerId?: string,
  options?: WidgetLaunchOptions
];

export const getEmbeddedWidgetOptions = (
  containerId?: string
): EmbeddedWidgetOptions => (containerId ? { enableIframeResize: false } : {});

export const getEmbeddedContainerArgs = (containerId?: string): [string] | [] =>
  containerId ? [containerId] : [];

export const getEmbeddedWidgetCallArgs = (
  containerId: string | undefined,
  options: WidgetLaunchOptions = {}
): EmbeddedWidgetCallArgs => {
  const hasRefreshHandler = Boolean(options.onCustomerAttributionTokenRefresh);

  if (containerId) {
    return hasRefreshHandler ? [containerId, options] : [containerId];
  }

  return hasRefreshHandler ? [undefined, options] : [];
};

export const getCustomerAttributionWidgetOptions = (
  params: WidgetQueryParams,
  options: WidgetLaunchOptions = {},
  mirroredParams: WidgetQueryParams[] = []
):
  | Pick<
      WidgetOptions,
      | "onCustomerAttributionTokenRefresh"
      | "onCustomerAttributionTokenRefreshAccepted"
    >
  | Record<string, never> => {
  const refreshHandler = options.onCustomerAttributionTokenRefresh;
  if (!refreshHandler) return {};

  return {
    onCustomerAttributionTokenRefresh: async () => {
      try {
        const value = await refreshHandler();
        const token = typeof value === "string" ? value.trim() : "";
        return token || undefined;
      } catch {
        // A refresh failure intentionally falls back to an unattributed checkout.
      }

      return undefined;
    },
    onCustomerAttributionTokenRefreshAccepted: (token: string) => {
      [params, ...mirroredParams].forEach((target) => {
        Object.assign(target, {
          [CUSTOMER_ATTRIBUTION_QUERY_PARAM]: token,
        });
      });
    },
  };
};
